/**
 * UserApiClient
 * 
 * Handles user-specific API operations including addresses
 */

import { BaseApiClient } from './BaseApiClient';

export interface UserAddress {
  id?: string;
  address: string;
  detail?: string;
  lat: number;
  lng: number;
  isPrimary?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export class UserApiClient extends BaseApiClient {
  /**
   * Get all user addresses
   */
  async getUserAddresses(): Promise<UserAddress[]> {
    const response = await this.get<{ addresses?: UserAddress[]; data?: { addresses?: UserAddress[] } } | UserAddress[]>(
      '/user/addresses'
    );
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.addresses)) {
      return response.addresses;
    }
    if (Array.isArray(response.data?.addresses)) {
      return response.data.addresses;
    }
    return [];
  }

  /**
   * Add a new user address
   */
  async addUserAddress(addressData: Omit<UserAddress, 'id' | 'createdAt' | 'updatedAt'>): Promise<UserAddress | null> {
    const response = await this.post<{ address?: UserAddress; data?: { address?: UserAddress } }>(
      '/user/addresses',
      addressData
    );
    return response.address ?? response.data?.address ?? null;
  }

  /**
   * Update an existing user address
   */
  async updateUserAddress(addressId: string, updates: Partial<UserAddress>): Promise<UserAddress | null> {
    const response = await this.patch<{ address?: UserAddress; data?: { address?: UserAddress } }>(
      `/user/addresses/${addressId}`,
      updates
    );
    return response.address ?? response.data?.address ?? null;
  }

  /**
   * Delete a user address
   */
  async deleteUserAddress(addressId: string): Promise<{ deleted: boolean }> {
    return this.delete<{ deleted: boolean }>(`/user/addresses/${addressId}`);
  }

  /**
   * Set an address as primary
   */
  async setPrimaryUserAddress(addressId: string): Promise<{ updated: boolean }> {
    return this.post<{ updated: boolean }>(`/user/addresses/${addressId}/primary`, {});
  }

  /**
   * Get public user profile with role-specific profiles
   * Returns basic user information plus any role-specific public profiles (e.g., MERCHANT profile)
   * 
   * This is a public endpoint that requires only client-key (no authentication).
   * Only published role-specific profiles are returned.
   * 
   * @param userId - User ID to fetch profile for
   * @returns Public user profile with role-specific profiles
   * @throws Error if the request fails or user is not found
   * 
   * @example
   * ```typescript
   * const profile = await platformApi.user.getPublicUserProfile('user123');
   * const merchantProfile = profile.public?.otherProfiles?.find(p => p.role === 'MERCHANT');
   * ```
   */
  async getPublicUserProfile(userId: string): Promise<PublicUserProfile> {
    if (!userId || typeof userId !== 'string') {
      throw new Error('User ID is required and must be a string');
    }

    // Picker public route — /api/v1/user/profile/... is not registered (returns HTML shell if proxied wrong).
    const url = `/api/v1/public/user/profile/${encodeURIComponent(userId)}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const rawText = await response.text();
      const normalized = rawText.replace(/^\uFEFF/, '');
      const trimmed = normalized.trim();

      if (!response.ok) {
        let errorMessage = `Failed to fetch public user profile: ${response.status}`;
        if (trimmed.startsWith('{')) {
          try {
            const errorJson = JSON.parse(trimmed) as {
              message?: string;
              error?: { message?: string };
            };
            errorMessage += ` - ${errorJson.message || errorJson.error?.message || trimmed}`;
          } catch {
            errorMessage += ` - ${trimmed.slice(0, 200)}`;
          }
        } else if (trimmed.startsWith('<')) {
          errorMessage +=
            ' - Server returned HTML (often a proxy or routing misconfiguration), not JSON';
        } else {
          errorMessage += trimmed ? ` - ${trimmed.slice(0, 200)}` : ' - (empty body)';
        }
        throw new Error(errorMessage);
      }

      if (!trimmed) {
        throw new Error('USER_PROFILE_EMPTY_BODY');
      }
      if (trimmed.startsWith('<')) {
        throw new Error(
          'USER_PROFILE_HTML_RESPONSE: public user profile endpoint returned HTML instead of JSON (check API path and proxy)'
        );
      }

      let data: unknown;
      try {
        data = JSON.parse(trimmed);
      } catch {
        throw new Error('USER_PROFILE_INVALID_JSON');
      }

      return data as PublicUserProfile;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        `Failed to fetch public user profile: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }
}

/**
 * Public User Profile Interface
 * Based on API Reference Manual: GET /api/v1/public/user/profile/:userId (nested alias of public payee profile)
 */
export interface PublicUserProfile {
  id: string;
  username: string;
  createdAt: string;
  public?: {
    otherProfiles?: Array<{
      role: string; // e.g., "MERCHANT", "BANK"
      profile: any; // Role-specific profile object (structure varies by role)
    }>;
  };
}
